import { format as dateFnsFormat } from 'date-fns'
import { useMemo } from 'react'

import { useLocaleContext } from '@src/components/locale/locale.context'
import { IS_DEV_ENV, IS_PROD_BUILD } from '@src/config/env'
import { DEFAULT_LOCALE } from '@src/config/localisation/locale-config.constants'
import { TAbsoluteLocale } from '@src/config/localisation/locale-config.types'
import { FEATURES, MODULES } from '@src/quince-site-common/src/constants/template-info'
import { captureException } from '@src/quince-site-common/src/utils/sentryUtil'

import {
  BASE_DATE_FORMATS,
  getDateFnsLocale,
  TIME_FORMATS,
  type DateFormatName,
  type FormattedDateResult,
  type UseFormattedDateOptions,
} from './date-time.constants'

/**
 * Check if a date value produces a valid Date object
 */
export function isValidDate(date: Date | number | string): boolean {
  const dateObj = date instanceof Date ? date : new Date(date)
  return !isNaN(dateObj.getTime())
}

/**
 * Handle an invalid date by throwing in local dev, capturing to Datadog
 * in non-prod deployed environments, and logging a warning otherwise.
 */
function handleInvalidDate(date: Date | number | string, context?: Record<string, unknown>): void {
  const error = new Error(`[DateTime] Invalid date value: ${String(date)}`)

  if (IS_DEV_ENV) {
    throw error
  }

  if (!IS_PROD_BUILD) {
    captureException(error, {
      module: MODULES.UNKNOWN,
      feature: FEATURES.UNKNOWN,
      extraInfo: { date: String(date), ...context },
    })
  }
}

/**
 * Convert app locale to BCP 47 format for Intl API
 * This should be handled by the locale context provider, but we have a fallback here for backwards compatibility.
 */
function toIntlLocale(locale: string): string {
  const [language, country] = locale.split('-')
  if (!language || !country) return DEFAULT_LOCALE
  // Intl expects country in uppercase (e.g. en-US, fr-CA)
  return `${language}-${country.toUpperCase()}`
}

/**
 * Check if a format name includes time component
 */
export function formatIncludesTime(formatName: DateFormatName): boolean {
  return TIME_FORMATS.has(formatName)
}

/**
 * Convert a date to ISO 8601 format for the HTML time element's datetime attribute.
 * Returns full ISO string for dates with time, or just date portion for date-only formats.
 */
export function toISODatetime(date: Date | number | string, includeTime = true): string {
  const dateObj = date instanceof Date ? date : new Date(date)
  return includeTime ? dateObj.toISOString() : (dateObj.toISOString().split('T')[0] ?? '')
}

/**
 * Format a date using Intl.DateTimeFormat or date-fns override.
 * This is a standalone function for use outside React components.
 *
 * @param date - The date to format (Date object, timestamp, or date string)
 * @param formatName - The semantic format name
 * @param locale - The locale to use for formatting
 * @param options - Formatting options
 * @returns The formatted date string
 */
export function formatDate(
  date: Date | number | string,
  formatName: DateFormatName,
  locale: TAbsoluteLocale,
  options: UseFormattedDateOptions = {},
): string {
  const { overrides = {} } = options

  const dateObj = date instanceof Date ? date : new Date(date)
  const normalizedLocale = locale.toLowerCase() as TAbsoluteLocale

  // Check for override
  const override = overrides[normalizedLocale]?.[formatName]
  if (override) {
    switch (override.type) {
      case 'date-fns': {
        const dateFnsLocale = getDateFnsLocale(override.locale ?? normalizedLocale)
        return dateFnsFormat(dateObj, override.formatString, { locale: dateFnsLocale })
      }
      case 'intl': {
        const bcp47Locale = toIntlLocale(override.locale)
        const formatConfig = BASE_DATE_FORMATS[formatName]
        return new Intl.DateTimeFormat(bcp47Locale, formatConfig.options).format(dateObj)
      }
      default: {
        const _exhaustive: never = override
        throw new Error(`Unknown override type: ${JSON.stringify(_exhaustive)}`)
      }
    }
  }

  // Default to Intl.DateTimeFormat
  const bcp47Locale = toIntlLocale(locale)
  const formatConfig = BASE_DATE_FORMATS[formatName]
  return new Intl.DateTimeFormat(bcp47Locale, formatConfig.options).format(dateObj)
}

/**
 * Format a date and time together.
 *
 * @param date - The date to format
 * @param dateFormat - The date format name
 * @param timeFormat - The time format name
 * @param locale - The locale to use
 * @param separator - Separator between date and time (default: ' ')
 * @param options - Formatting options
 * @returns The formatted date and time string
 */
export function formatDateTime(
  date: Date | number | string,
  dateFormat: DateFormatName,
  timeFormat: DateFormatName,
  locale: TAbsoluteLocale,
  separator = ' ',
  options: UseFormattedDateOptions = {},
): string {
  const dateStr = formatDate(date, dateFormat, locale, options)
  const timeStr = formatDate(date, timeFormat, locale, options)
  return `${dateStr}${separator}${timeStr}`
}

/**
 * Hook for formatting a date using the Intl Web API with locale awareness.
 * Returns both the formatted date string and ISO datetime string.
 *
 * @param date - The date to format (Date object, timestamp, or date string)
 * @param formatName - The semantic format name
 * @param options - Optional formatting options
 * @returns Object containing formatted date and ISO datetime string
 *
 * @example
 * ```tsx
 * // Format a full date
 * const { formatted, iso } = useFormattedDate(new Date(), 'NUMERIC_SLASH_DATE')
 * // formatted: "01/08/2026"
 * // iso: "2026-01-08"
 *
 * // Format with time
 * const { formatted, iso } = useFormattedDate(new Date(), 'SHORT_MONTH_DAY_AND_TIME_12H')
 * // formatted: "Jan 08 3:45 PM"
 * // iso: "2026-01-08T15:45:00.000Z"
 * ```
 *
 */
export function useFormattedDate(
  date: Date | number | string,
  formatName: DateFormatName,
  options: UseFormattedDateOptions = {},
): FormattedDateResult | null {
  const { locale } = useLocaleContext()

  return useMemo(() => {
    if (!isValidDate(date)) {
      handleInvalidDate(date, { formatName })
      return null
    }

    const formatted = formatDate(date, formatName, locale, options)
    const includeTime = formatIncludesTime(formatName)
    const iso = toISODatetime(date, includeTime)
    return { formatted, iso }
  }, [date, formatName, locale, options])
}
